import { useState, useEffect, useCallback } from 'react';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { 
  Building2, Globe, Mail, Phone, Save, CheckCircle, Loader2, X, Plus, Tag, 
  Upload, ChevronLeft, ChevronRight, Shield, Users, Briefcase, Eye, 
  Award, FileText, MapPin, Clock, Languages, Monitor, UserCheck
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const SERVICE_OPTIONS = [
  'MFA Implementatie', 'Identity Management', 'Access Control', 'Security Audit',
  'Phishing Training', 'Security Awareness', 'Penetration Testing',
  'Vulnerability Assessment', 'ISO27001', 'NIS2', 'GDPR Compliance',
  'Incident Response', 'SOC Services', 'Cloud Security', 'Network Security',
  'Firewall', 'Backup & Recovery', 'Data Protection', 'SIEM/SOC',
];

const EXPERTISE_OPTIONS = [
  'Cyber risicomanagement', 'Security architectuur', 'Compliance advies',
  'Technische beveiliging', 'Bewustzijnstraining', 'Incident management',
  'Privacy & GDPR', 'Cloud security', 'OT/ICS beveiliging', 'Supply chain security',
];

const ONDERSTEUNING_OPTIONS = [
  'Advies', 'Implementatie', 'Beheer', 'Training', 'Monitoring', 'Auditvoorbereiding', 'Analyse',
];

const FASE_OPTIONS = [
  'Analyse', 'Advies', 'Implementatie', 'Beheer', 'Training', 'Monitoring', 'Auditvoorbereiding',
];

const SECTOR_OPTIONS = [
  'Overheid', 'Gezondheidszorg', 'Financiële diensten', 'Retail',
  'Productie', 'Technologie', 'Onderwijs', 'Transport & Logistiek',
  'Energie', 'Non-profit', 'Juridisch', 'Consultancy', 'MKB Algemeen',
];

const REGION_OPTIONS = [
  'Noord-Nederland', 'Oost-Nederland', 'Zuid-Nederland', 'West-Nederland',
  'Randstad', 'Heel Nederland', 'België', 'Duitsland', 'Europa',
];

const TAAL_OPTIONS = ['Nederlands', 'Engels', 'Duits', 'Frans'];

const WIZARD_STEPS = [
  { id: 'bedrijf', title: 'Bedrijfsinformatie', icon: Building2 },
  { id: 'diensten', title: 'Diensten & Expertise', icon: Tag },
  { id: 'afbakening', title: 'Afbakening', icon: Shield },
  { id: 'onderbouwing', title: 'Onderbouwing', icon: Award },
  { id: 'presentatie', title: 'Klantpresentatie', icon: Eye },
  { id: 'controle', title: 'Controle & Indienen', icon: CheckCircle },
];

// Tag input component
const TagField = ({ label, options, values, onChange, freeText = false, placeholder }) => {
  const [input, setInput] = useState('');
  const add = (val) => { if (val && !values.includes(val)) { onChange([...values, val]); } setInput(''); };
  const remove = (val) => onChange(values.filter(v => v !== val));

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {values.map(v => (
            <Badge key={v} variant="secondary" className="pr-1 text-xs">
              {v}
              <button onClick={() => remove(v)} className="ml-1 hover:text-red-500"><X className="h-3 w-3" /></button>
            </Badge>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        {options ? (
          <select value={input} onChange={e => setInput(e.target.value)}
            className="flex-1 h-9 px-3 rounded-md border border-neutral-200 bg-white text-sm">
            <option value="">{placeholder || 'Selecteer...'}</option>
            {options.filter(o => !values.includes(o)).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <Input value={input} onChange={e => setInput(e.target.value)} placeholder={placeholder}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(input); } }} className="h-9 text-sm" />
        )}
        <Button type="button" variant="outline" size="sm" onClick={() => add(input)} className="h-9 px-3">
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

const SupplierProfilePage = () => {
  const { api } = useSupplierAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [completeness, setCompleteness] = useState(null);
  const [submitDialog, setSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    company_name: '', kvk_number: '', vestigingsplaats: '', website: '',
    contact_email: '', contactpersoon: '', contact_phone: '',
    services: [], expertises: [], type_ondersteuning: [], ondersteunde_fasen: [],
    doelgroepen: [], sectorervaring: [], regions: [],
    complexiteitsniveau: '', opdracht_omvang: '',
    wat_wel: '', wat_niet: '', niet_geschikt_voor: '', min_max_projectomvang: '',
    certificeringen: [], keurmerken: [], referentiecases: '', jaren_ervaring: '',
    teamgrootte: '', partnerschappen: '',
    beschikbaar: true, gemiddelde_responstijd: '', talen: [],
    remote_mogelijk: true, op_locatie_mogelijk: true,
    korte_pitch: '', bedrijfsomschrijving: '', aanpak_werkwijze: '', onderscheidend: '',
  });

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const fetchProfile = useCallback(async () => {
    try {
      const [profileRes, compRes] = await Promise.all([
        api.get('/profile'),
        api.get('/profile/completeness'),
      ]);
      const p = profileRes.data.profile;
      setForm(prev => {
        const merged = { ...prev };
        Object.keys(prev).forEach(k => { if (p[k] !== undefined && p[k] !== null) merged[k] = p[k]; });
        return merged;
      });
      setCompleteness(compRes.data);
      if (p.wizard_step !== undefined) setStep(p.wizard_step);
    } catch (e) {
      console.error('Profile fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const saveProgress = async (nextStep) => {
    setSaving(true);
    try {
      const res = await api.patch('/profile/wizard', { ...form, wizard_step: nextStep ?? step });
      setCompleteness(res.data.completeness);
      toast.success('Opgeslagen');
    } catch (e) {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    if (step < WIZARD_STEPS.length - 1) {
      await saveProgress(step + 1);
      setStep(step + 1);
    }
  };

  const goPrev = () => { if (step > 0) setStep(step - 1); };

  const handleSubmitForReview = async () => {
    setSubmitting(true);
    try {
      await saveProgress(step);
      await api.post('/profile/submit-review');
      toast.success('Profiel ingediend voor beoordeling!');
      setSubmitDialog(false);
      fetchProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Indienen mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
    </div>
  );

  const pct = completeness?.percentage || 0;

  // Step renderers
  const renderBedrijf = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label>Bedrijfsnaam *</Label><Input value={form.company_name} onChange={e => set('company_name', e.target.value)} data-testid="wiz-company-name" /></div>
        <div className="space-y-1.5"><Label>KvK-nummer *</Label><Input value={form.kvk_number} onChange={e => set('kvk_number', e.target.value)} data-testid="wiz-kvk" /></div>
        <div className="space-y-1.5"><Label>Vestigingsplaats *</Label><Input value={form.vestigingsplaats} onChange={e => set('vestigingsplaats', e.target.value)} placeholder="bijv. Amsterdam" data-testid="wiz-vestiging" /></div>
        <div className="space-y-1.5"><Label>Website *</Label><Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." data-testid="wiz-website" /></div>
        <div className="space-y-1.5"><Label>Contactpersoon *</Label><Input value={form.contactpersoon} onChange={e => set('contactpersoon', e.target.value)} data-testid="wiz-contactpersoon" /></div>
        <div className="space-y-1.5"><Label>Zakelijk e-mailadres *</Label><Input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} data-testid="wiz-email" /></div>
        <div className="space-y-1.5"><Label>Telefoonnummer *</Label><Input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} data-testid="wiz-phone" /></div>
      </div>
    </div>
  );

  const renderDiensten = () => (
    <div className="space-y-5">
      <TagField label="Diensten *" options={SERVICE_OPTIONS} values={form.services} onChange={v => set('services', v)} placeholder="Selecteer dienst..." />
      <TagField label="Expertises *" options={EXPERTISE_OPTIONS} values={form.expertises} onChange={v => set('expertises', v)} placeholder="Selecteer expertise..." />
      <TagField label="Type ondersteuning *" options={ONDERSTEUNING_OPTIONS} values={form.type_ondersteuning} onChange={v => set('type_ondersteuning', v)} placeholder="Selecteer type..." />
      <TagField label="Ondersteunde fasen" options={FASE_OPTIONS} values={form.ondersteunde_fasen} onChange={v => set('ondersteunde_fasen', v)} placeholder="Selecteer fase..." />
      <TagField label="Sectorervaring *" options={SECTOR_OPTIONS} values={form.sectorervaring} onChange={v => set('sectorervaring', v)} placeholder="Selecteer sector..." />
      <TagField label="Werkgebied / Regio's *" options={REGION_OPTIONS} values={form.regions} onChange={v => set('regions', v)} placeholder="Selecteer regio..." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Complexiteitsniveau</Label>
          <select value={form.complexiteitsniveau} onChange={e => set('complexiteitsniveau', e.target.value)}
            className="w-full h-9 px-3 rounded-md border border-neutral-200 bg-white text-sm">
            <option value="">Selecteer...</option>
            <option value="basis">Basis</option>
            <option value="gevorderd">Gevorderd</option>
            <option value="complex">Complex</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Omvang opdrachten</Label>
          <select value={form.opdracht_omvang} onChange={e => set('opdracht_omvang', e.target.value)}
            className="w-full h-9 px-3 rounded-md border border-neutral-200 bg-white text-sm">
            <option value="">Selecteer...</option>
            <option value="klein">Klein (&lt; 10K)</option>
            <option value="middel">Middel (10K - 50K)</option>
            <option value="groot">Groot (50K - 200K)</option>
            <option value="enterprise">Enterprise (&gt; 200K)</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderAfbakening = () => (
    <div className="space-y-5">
      <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
        <p className="text-xs text-amber-700">Afbakening is verplicht. Dit voorkomt overselling en zorgt dat klanten alleen relevante leveranciers zien.</p>
      </div>
      <div className="space-y-1.5">
        <Label>Wat doet u expliciet WEL *</Label>
        <Textarea value={form.wat_wel} onChange={e => set('wat_wel', e.target.value)} rows={3}
          placeholder="Beschrijf concreet welke diensten en trajecten u aanbiedt..." data-testid="wiz-wat-wel" />
      </div>
      <div className="space-y-1.5">
        <Label>Wat doet u expliciet NIET *</Label>
        <Textarea value={form.wat_niet} onChange={e => set('wat_niet', e.target.value)} rows={3}
          placeholder="Beschrijf wat u niet aanbiedt of buiten uw expertise valt..." data-testid="wiz-wat-niet" />
      </div>
      <div className="space-y-1.5">
        <Label>Voor welke trajecten bent u niet geschikt</Label>
        <Textarea value={form.niet_geschikt_voor} onChange={e => set('niet_geschikt_voor', e.target.value)} rows={2}
          placeholder="bijv. Niet geschikt voor enterprise SOC-implementaties boven 500 gebruikers..." />
      </div>
      <div className="space-y-1.5">
        <Label>Minimale / maximale projectomvang</Label>
        <Input value={form.min_max_projectomvang} onChange={e => set('min_max_projectomvang', e.target.value)}
          placeholder="bijv. Minimaal 5K, maximaal 100K per traject" />
      </div>
    </div>
  );

  const renderOnderbouwing = () => (
    <div className="space-y-5">
      <TagField label="Certificeringen *" values={form.certificeringen} onChange={v => set('certificeringen', v)}
        placeholder="bijv. ISO 27001, SOC 2, CISA..." />
      <TagField label="Keurmerken" values={form.keurmerken || []} onChange={v => set('keurmerken', v)}
        placeholder="bijv. CCV Keurmerk, NOREA..." />
      <div className="space-y-1.5">
        <Label>Referentiecases</Label>
        <Textarea value={form.referentiecases} onChange={e => set('referentiecases', e.target.value)} rows={3}
          placeholder="Beschrijf 1-3 relevante projecten die u heeft uitgevoerd..." />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label>Aantal jaar ervaring</Label><Input type="number" value={form.jaren_ervaring} onChange={e => set('jaren_ervaring', e.target.value)} placeholder="bijv. 10" /></div>
        <div className="space-y-1.5">
          <Label>Teamgrootte</Label>
          <select value={form.teamgrootte} onChange={e => set('teamgrootte', e.target.value)}
            className="w-full h-9 px-3 rounded-md border border-neutral-200 bg-white text-sm">
            <option value="">Selecteer...</option>
            <option value="1-5">1-5 medewerkers</option>
            <option value="6-20">6-20 medewerkers</option>
            <option value="21-50">21-50 medewerkers</option>
            <option value="50+">50+ medewerkers</option>
          </select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Partnerschappen</Label>
        <Input value={form.partnerschappen} onChange={e => set('partnerschappen', e.target.value)}
          placeholder="bijv. Microsoft Gold Partner, AWS Partner..." />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={form.beschikbaar} onChange={e => set('beschikbaar', e.target.checked)} className="h-4 w-4 rounded" />
          <Label className="text-sm">Accepteert nieuwe aanvragen</Label>
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={form.remote_mogelijk} onChange={e => set('remote_mogelijk', e.target.checked)} className="h-4 w-4 rounded" />
          <Label className="text-sm">Remote mogelijk</Label>
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={form.op_locatie_mogelijk} onChange={e => set('op_locatie_mogelijk', e.target.checked)} className="h-4 w-4 rounded" />
          <Label className="text-sm">Op locatie mogelijk</Label>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label>Gemiddelde responstijd</Label><Input value={form.gemiddelde_responstijd} onChange={e => set('gemiddelde_responstijd', e.target.value)} placeholder="bijv. Binnen 24 uur" /></div>
        <TagField label="Talen" options={TAAL_OPTIONS} values={form.talen || []} onChange={v => set('talen', v)} placeholder="Selecteer taal..." />
      </div>
    </div>
  );

  const renderPresentatie = () => (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label>Korte pitch *</Label>
        <Textarea value={form.korte_pitch} onChange={e => set('korte_pitch', e.target.value)} rows={2}
          placeholder="1-2 zinnen waarmee u zich voorstelt aan potentiële klanten..." data-testid="wiz-pitch" />
        <p className="text-[10px] text-neutral-400">{form.korte_pitch?.length || 0}/200 tekens</p>
      </div>
      <div className="space-y-1.5">
        <Label>Bedrijfsomschrijving *</Label>
        <Textarea value={form.bedrijfsomschrijving} onChange={e => set('bedrijfsomschrijving', e.target.value)} rows={4}
          placeholder="Uitgebreidere beschrijving van uw bedrijf, aanpak en ervaring..." data-testid="wiz-beschrijving" />
      </div>
      <div className="space-y-1.5">
        <Label>Aanpak / werkwijze</Label>
        <Textarea value={form.aanpak_werkwijze} onChange={e => set('aanpak_werkwijze', e.target.value)} rows={3}
          placeholder="Hoe pakt u een typisch traject aan? Welke methodiek gebruikt u?" />
      </div>
      <div className="space-y-1.5">
        <Label>Waarom deze leverancier onderscheidend is</Label>
        <Textarea value={form.onderscheidend} onChange={e => set('onderscheidend', e.target.value)} rows={2}
          placeholder="Wat maakt u anders dan andere leveranciers in uw vakgebied?" />
      </div>
    </div>
  );

  const renderControle = () => {
    const missing = completeness?.missing || [];
    return (
      <div className="space-y-6">
        {/* Completeness */}
        <div className="p-5 rounded-xl border border-neutral-200 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-black">Profielvolledigheid</span>
            <span className={`text-2xl font-bold ${pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-amber-600' : 'text-red-500'}`}>{pct}%</span>
          </div>
          <Progress value={pct} className="h-2" />
          <p className="text-xs text-neutral-500">Minimaal 60% vereist om in te dienen voor beoordeling.</p>
        </div>

        {/* Missing fields */}
        {missing.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Ontbrekende velden</p>
            <div className="grid grid-cols-2 gap-2">
              {missing.map(m => (
                <div key={m.field} className="flex items-center gap-2 text-sm text-red-600 p-2 rounded-lg bg-red-50">
                  <X className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                  {m.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="space-y-2">
          <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Profiel samenvatting</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-neutral-50"><span className="text-neutral-500">Bedrijf:</span> <strong>{form.company_name || '-'}</strong></div>
            <div className="p-3 rounded-lg bg-neutral-50"><span className="text-neutral-500">Diensten:</span> <strong>{form.services?.length || 0}</strong></div>
            <div className="p-3 rounded-lg bg-neutral-50"><span className="text-neutral-500">Expertises:</span> <strong>{form.expertises?.length || 0}</strong></div>
            <div className="p-3 rounded-lg bg-neutral-50"><span className="text-neutral-500">Regio's:</span> <strong>{form.regions?.length || 0}</strong></div>
            <div className="p-3 rounded-lg bg-neutral-50"><span className="text-neutral-500">Certificeringen:</span> <strong>{form.certificeringen?.length || 0}</strong></div>
            <div className="p-3 rounded-lg bg-neutral-50"><span className="text-neutral-500">Afbakening:</span> <strong>{form.wat_wel && form.wat_niet ? 'Ingevuld' : 'Ontbreekt'}</strong></div>
          </div>
        </div>

        {/* Submit CTA */}
        <Button
          className="w-full bg-black text-white hover:bg-neutral-800 h-11"
          disabled={pct < 60}
          onClick={() => setSubmitDialog(true)}
          data-testid="wiz-submit-review"
        >
          <FileText className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Indienen voor beoordeling
        </Button>
        {pct < 60 && <p className="text-xs text-red-500 text-center">Vul minimaal 60% van de verplichte velden in om in te dienen.</p>}
      </div>
    );
  };

  const STEP_RENDERERS = [renderBedrijf, renderDiensten, renderAfbakening, renderOnderbouwing, renderPresentatie, renderControle];
  const currentStep = WIZARD_STEPS[step];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">Profiel Wizard</p>
          <h1 className="text-2xl font-bold tracking-tight text-black">Profiel opbouwen</h1>
          <p className="text-sm text-neutral-500 mt-1">Vul uw profiel stap voor stap in om zichtbaar te worden voor klanten</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs text-neutral-400">Volledigheid</p>
            <p className={`text-lg font-bold ${pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-amber-600' : 'text-neutral-400'}`}>{pct}%</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => saveProgress(step)} disabled={saving} data-testid="wiz-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" strokeWidth={1.5} />}
            Opslaan
          </Button>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-0 overflow-x-auto" data-testid="profile-wizard-stepper">
        {WIZARD_STEPS.map((s, i) => {
          const StepIcon = s.icon;
          const active = i === step;
          const done = i < step;
          return (
            <button key={s.id} onClick={() => setStep(i)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                active ? 'bg-black text-white' : done ? 'bg-green-50 text-green-700' : 'text-neutral-400 hover:bg-neutral-50'
              }`} data-testid={`wiz-step-${s.id}`}>
              {done ? <CheckCircle className="h-4 w-4" strokeWidth={1.5} /> : <StepIcon className="h-4 w-4" strokeWidth={1.5} />}
              <span className="hidden lg:inline">{s.title}</span>
              <span className="lg:hidden">Stap {i + 1}</span>
            </button>
          );
        })}
      </div>

      {/* Content Card */}
      <Card className="border-neutral-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-neutral-100">
            <currentStep.icon className="h-5 w-5 text-neutral-400" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-bold text-black">{currentStep.title}</p>
              <p className="text-xs text-neutral-400">Stap {step + 1} van {WIZARD_STEPS.length}</p>
            </div>
          </div>
          {STEP_RENDERERS[step]()}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" size="sm" disabled={step === 0} onClick={goPrev} data-testid="wiz-prev">
          <ChevronLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Vorige
        </Button>
        <div className="flex items-center gap-1.5">
          {WIZARD_STEPS.map((ws, i) => (
            <button key={ws.id} onClick={() => setStep(i)}
              className={`h-2 rounded-full transition-all ${step === i ? 'w-6 bg-black' : i < step ? 'w-3 bg-green-400' : 'w-2 bg-neutral-200'}`} />
          ))}
        </div>
        {step < WIZARD_STEPS.length - 1 ? (
          <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={goNext} disabled={saving} data-testid="wiz-next">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Volgende <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50"
            onClick={() => pct >= 60 ? setSubmitDialog(true) : toast.info('Vul minimaal 60% in')} data-testid="wiz-finish">
            <CheckCircle className="h-4 w-4 mr-1" strokeWidth={1.5} /> Afronden
          </Button>
        )}
      </div>

      {/* Submit Dialog */}
      <Dialog open={submitDialog} onOpenChange={setSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Profiel indienen voor beoordeling</DialogTitle>
            <DialogDescription>
              Uw profiel wordt beoordeeld door Spooker op specificiteit, realisme en matchbaarheid. Na goedkeuring wordt u zichtbaar voor klanten.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-neutral-500">Volledigheid</span>
              <span className="font-bold">{pct}%</span>
            </div>
            <Progress value={pct} className="h-2" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitDialog(false)}>Annuleren</Button>
            <Button onClick={handleSubmitForReview} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Indienen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierProfilePage;
